import {
  IPayableSchema,
  IPayrollSettings,
  ScheduleStatus
} from "@wingspanhq/payments/dist/interfaces";
import {
  IScheduleDate,
  IScheduleDateUpdate
} from "@wingspanhq/payments/dist/interfaces/invoice";
import { wsMoment } from "@wingspanhq/utils/dist/date/wsMoment";

const UTC_PAYROLL_CUTOFF_HOUR = 17;
const UTC_PAYROLL_CUTOFF_MINUTES = 30;

export const getExactCutOffDate = (cutOffDate: Date) => {
  return new Date(
    Date.UTC(
      cutOffDate.getUTCFullYear(),
      cutOffDate.getUTCMonth(),
      cutOffDate.getUTCDate(),
      UTC_PAYROLL_CUTOFF_HOUR,
      UTC_PAYROLL_CUTOFF_MINUTES
    )
  );
};

/**
 * Finds the schedule whose run is currently in flight, falling back to the
 * next upcoming Pending/Modified one. A schedule is "currently processing"
 * when its pay date is today or in the future AND its run has already started.
 * A run has started when either:
 *   - the backend flipped it to Completed (it does so the moment the run
 *     begins, well before individual payables actually settle), or
 *   - it is still Pending/Modified but its modeled cutoff has passed (the
 *     brief pre-flip window).
 * Skipped runs are excluded — they were opted out, not processing (PLA-2870).
 *
 * Keying "started" off the Completed status — not only the modeled cutoff —
 * matters: getExactCutOffDate anchors the cutoff at a fixed UTC hour, so in
 * summer (EDT) it resolves ~1h later than the backend's real processing start.
 * A schedule the backend has already marked Completed is in flight even if
 * that modeled cutoff has not yet elapsed, so relying on the cutoff alone left
 * an early-afternoon gap where payables still rolled to the next pay period.
 *
 * The pay-date filter also drops stuck-Pending entries from old off-cycle
 * runs (their date is in the past), which is what previously required a
 * 5-business-day cutoff guard (PLA-2734, PLA-2741).
 */
export const getCurrentOrNextPayroll = (payrollSettings: IPayrollSettings) => {
  const now = new Date();
  const startOfToday = wsMoment.utc(now).startOf("day").toDate();

  const next = payrollSettings.scheduleDates?.find(
    s =>
      (s.status === ScheduleStatus.Pending ||
        s.status === ScheduleStatus.Modified) &&
      s.cutOffDate &&
      getExactCutOffDate(s.cutOffDate) > now
  );

  // Require an upcoming schedule before treating a past-cutoff entry as
  // "currently processing" — if the whole array is past-cutoff, the data is
  // stuck and we should return nothing rather than a stale run.
  const currentlyProcessing = next
    ? payrollSettings.scheduleDates
        ?.filter(
          s =>
            s.status !== ScheduleStatus.Skipped &&
            s.date &&
            new Date(s.date) >= startOfToday &&
            // Run has started: backend already flipped to Completed, or the
            // modeled cutoff has passed while it is still Pending/Modified.
            (s.status === ScheduleStatus.Completed ||
              (!!s.cutOffDate && getExactCutOffDate(s.cutOffDate) <= now))
        )
        .pop()
    : undefined;

  return currentlyProcessing ?? next;
};

/**
 * Finds the payroll schedule that will include a given payable.
 *
 * When enableProcessDaysBeforeDue is OFF, the backend applies no date filter —
 * all approved invoices go into the next run. So we return getCurrentOrNextPayroll.
 *
 * When ON, mirrors the backend's bufferedCheckDate logic:
 *   bufferedCheckDate = scheduleDate.date + processDaysBeforeDue + 1 business day
 * A payable belongs to the first pending schedule whose bufferedCheckDate
 * exceeds the payable's effective due date (client.payDate || dueDate).
 */
export const getMatchingPayroll = (
  payrollSettings: IPayrollSettings,
  payable: IPayableSchema
) => {
  if (!payrollSettings.enableProcessDaysBeforeDue) {
    return getCurrentOrNextPayroll(payrollSettings);
  }

  const effectiveDueDate = payable.client.payDate || payable.dueDate;

  const processDaysBeforeDue = payrollSettings.processDaysBeforeDue ?? 0;

  return payrollSettings.scheduleDates?.find(scheduleDate => {
    if (
      scheduleDate.status !== ScheduleStatus.Pending &&
      scheduleDate.status !== ScheduleStatus.Modified
    ) {
      return false;
    }

    const bufferedCheckDate = wsMoment
      .utc(scheduleDate.date)
      .businessAdd(processDaysBeforeDue + 1)
      .toDate();

    return effectiveDueDate < bufferedCheckDate;
  });
};
